import { Modal } from '@shared/ui';
import { Button, Checkbox, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CheckboxIconRoot,
  Statement,
  StatementList,
  StatementText,
  StatementsWrapper,
} from './IndependenceConfirmationPopup.styles';
import { IndependenceCheckmarkIcon } from './icons';

type IndependenceConfirmationPopupProps = {
  open: boolean;
  confirmLoading?: boolean;
  conflictLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onConflict: () => void;
};

const CheckboxIcon = ({ checked }: { checked?: boolean }) => (
  <CheckboxIconRoot checked={checked}>
    <IndependenceCheckmarkIcon />
  </CheckboxIconRoot>
);

export default function IndependenceConfirmationPopup({
  open,
  confirmLoading,
  conflictLoading,
  onClose,
  onConfirm,
  onConflict,
}: IndependenceConfirmationPopupProps) {
  const { t, i18n } = useTranslation('dashboard', { keyPrefix: 'independence' });
  const statements = useMemo(
    () => t('statements', { returnObjects: true }) as string[],
    [i18n.language, t],
  );
  const [checkedItems, setCheckedItems] = useState<boolean[]>(() => statements.map(() => false));

  useEffect(() => {
    if (open) {
      setCheckedItems(statements.map(() => false));
    }
  }, [open, statements]);

  const handleToggle = (index: number) => {
    setCheckedItems((prev) => prev.map((value, idx) => (idx === index ? !value : value)));
  };

  const allChecked = checkedItems.length > 0 && checkedItems.every(Boolean);

  return (
    <Modal
      className='independence-popup'
      data-testid='independence-confirmation-modal'
      open={open}
      fullWidth={false}
      onClose={onClose}
      maxWidth={700}
    >
      <Modal.Header title={t('title')} onClose={onClose} />
      <Modal.Body>
        <Stack spacing={2}>
          <Stack spacing={1.5}>
            <Typography variant='body2'>{t('intro')}</Typography>
            <Typography variant='body2' fontWeight={600}>
              {t('description')}
            </Typography>
          </Stack>

          <StatementsWrapper>
            <StatementList>
              {statements.map((statement, index) => (
                <Statement
                  key={`${statement}-${index}`}
                  data-testid={`independence-statement-${index}`}
                  control={
                    <Checkbox
                      checked={checkedItems[index] ?? false}
                      icon={<CheckboxIcon />}
                      checkedIcon={<CheckboxIcon checked />}
                      onChange={() => handleToggle(index)}
                      disableRipple
                      sx={{ p: 0 }}
                      data-testid={`independence-checkbox-${index}`}
                    />
                  }
                  label={<StatementText>{statement}</StatementText>}
                />
              ))}
            </StatementList>
          </StatementsWrapper>

          <Typography variant='body2' lineHeight={1.6}>
            <Trans t={t} i18nKey='helperText' components={{ b: <b /> }} />
          </Typography>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='contained'
          color='gray'
          onClick={onConflict}
          disabled={Boolean(conflictLoading)}
          loading={conflictLoading}
          data-testid='independence-conflict-button'
        >
          {t('conflictCta')}
        </Button>
        <Button
          variant='contained'
          onClick={onConfirm}
          disabled={!allChecked || Boolean(confirmLoading)}
          loading={confirmLoading}
          data-testid='independence-confirm-button'
        >
          {t('confirmCta')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
